import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { productFruits as productFruitsSdk } from 'product-fruits';

@Injectable({
  providedIn: 'root'
})
export class ProductFruitsService {
  private initialized = false;
  private readonly LANGUAGE_CODE = 'en'; // Default language code (ISO 2-letter code)

  constructor(private readonly router: Router) {}

  private getProductFruitsInstance(): any {
    const instance = productFruitsSdk || (window as any).productFruits;

    if (!instance) {
      console.warn('Product Fruits SDK instance is not available');
      return null;
    }

    return instance;
  }

  /**
   * Initialize Product Fruits with user information
   * This should be called after user authentication is complete
   * 
   * @param username - The unique user identifier (required)
   * @param userProperties - Additional user properties (optional)
   * @param languageCode - ISO 2-letter language code (optional, defaults to 'en')
   */
  public initialize(
    username: string,
    userProperties?: Record<string, any>,
    languageCode?: string
  ): void {
    if (this.initialized) {
      console.warn('Product Fruits is already initialized');
      return;
    }

    if (!username) {
      console.error('Product Fruits: username is required for initialization');
      return;
    }

    try {
      const lang = languageCode || this.LANGUAGE_CODE;
      const workspaceCode = environment.production
        ? (window as any)['jupiter']?.workspaceCode
        : environment.workspaceCode;

      if (!workspaceCode) {
        console.error('Product Fruits: workspaceCode is required in configuration');
        return;
      }

      const userInfo = {
        username,
        ...userProperties
      };

      const productFruitsInstance = this.getProductFruitsInstance();
      if (!productFruitsInstance) {
        return;
      }

      // Initialize Product Fruits
      productFruitsInstance.init(
        workspaceCode,
        lang,
        userInfo
      );

      this.initialized = true;

      // Set up automatic page change detection for SPA
      this.setupRouteChangeDetection();

      console.log('Product Fruits initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Product Fruits:', error);
    }
  }

  /**
   * Set up automatic route change detection for single-page application
   * This ensures Product Fruits properly tracks page changes in the router
   */
  private setupRouteChangeDetection(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        // Notify Product Fruits of page change after route has been activated
        setTimeout(() => {
          this.safeExec(($productFruits) => {
            $productFruits.push(['pageChanged']);
          });
        }, 100);
      });
  }

  /**
   * Execute Product Fruits API methods safely
   * This ensures the API is ready before executing callbacks
   * 
   * @param callback - Function to execute when Product Fruits is ready
   */
  public safeExec(callback: (productFruits: any) => void): void {
    try {
      const productFruitsInstance = this.getProductFruitsInstance();
      if (!productFruitsInstance) {
        return;
      }

      productFruitsInstance.safeExec(callback);
    } catch (error) {
      console.error('Error executing Product Fruits API:', error);
    }
  }

  /**
   * Change the language for Product Fruits
   * Note: This destroys and reinitializes the instance with the new language
   * 
   * @param languageCode - ISO 2-letter language code
   * @param username - The user identifier (required if reinitializing)
   * @param userProperties - Additional user properties (optional)
   */
  public changeLanguage(
    languageCode: string,
    username: string,
    userProperties?: Record<string, any>
  ): void {
    try {
      // Destroy existing instance
      this.safeExec(($productFruits) => {
        if ($productFruits?.services?.destroy) {
          $productFruits.services.destroy();
        }
      });

      this.initialized = false;

      // Reinitialize with new language
      this.initialize(username, userProperties, languageCode);
    } catch (error) {
      console.error('Error changing Product Fruits language:', error);
    }
  }

  /**
   * Destroy Product Fruits instance
   * Use this when logging out or cleaning up
   */
  public destroy(): void {
    try {
      this.safeExec(($productFruits) => {
        if ($productFruits?.services?.destroy) {
          $productFruits.services.destroy();
        }
      });
      this.initialized = false;
    } catch (error) {
      console.error('Error destroying Product Fruits:', error);
    }
  }

  /**
   * Check if Product Fruits is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }
}
